// src/utils/schema.js
// Schema.org structured data for SEO

const BASE_URL = 'https://www.taxstatsid.com';

// NOTE: we deliberately publish NO AggregateRating in structured data. The
// Trustpilot score isn't shown as a number on the site (kept as a link only),
// so asserting a rating here would be an unsubstantiated review snippet — exactly
// what Google's review-snippet policy prohibits.

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "TaxStats ID",
  "alternateName": "TaxStats Identity Verification",
  "url": BASE_URL,
  "logo": `${BASE_URL}/logo.png`,
  "description": "Companies House Authorised Corporate Service Provider (ACSP) for identity verification of directors and PSCs",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "56 Oldham Road",
    "addressLocality": "Ashton-under-Lyne",
    "postalCode": "OL6 7AP",
    "addressCountry": "GB"
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+44-161-317-8556",
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    {
      "@type": "ContactPoint",
      "telephone": "+44-161-524-7124",
      "contactType": "customer service",
      "description": "AI Assistant - Available 24/7",
      "availableLanguage": "English"
    }
  ],
  "areaServed": "GB",
  "foundingDate": "2024",
  "knowsAbout": [
    "Companies House Identity Verification",
    "Director Verification",
    "PSC Verification",
    "ACSP Services"
  ],
  // Authoritative profiles that identify TaxStats as a real, verifiable entity —
  // a strong signal for search engines and AI assistants. Add the firm's
  // LinkedIn / YouTube / Google Business Profile URLs here when available.
  "sameAs": [
    "https://find-and-update.company-information.service.gov.uk/company/10445962",
    "https://taxstats.co.uk",
    "https://www.linkedin.com/company/taxstats",
    "https://www.youtube.com/@TaxStats",
    "https://uk.trustpilot.com/review/rjohnson.ltd"
  ]
};

// WebSite schema with a SearchAction — helps engines treat the site as an
// entity and can power a sitelinks search box.
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "TaxStats ID",
  "url": BASE_URL,
  "publisher": { "@type": "Organization", "name": "TaxStats ID", "url": BASE_URL },
  "potentialAction": {
    "@type": "SearchAction",
    "target": { "@type": "EntryPoint", "urlTemplate": `${BASE_URL}/glossary?q={search_term_string}` },
    "query-input": "required name=search_term_string"
  }
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Companies House Identity Verification",
  "provider": {
    "@type": "Organization",
    "name": "TaxStats ID",
    "url": BASE_URL
  },
  "description": "Official identity verification service for Companies House directors and Persons with Significant Control (PSCs)",
  "areaServed": "GB",
  "serviceType": "Identity Verification",
  "offers": [
    {
      "@type": "Offer",
      "name": "Digital Verification",
      "price": "39",
      "priceCurrency": "GBP",
      "description": "Online identity verification in 5-10 minutes using your smartphone",
      "availability": "https://schema.org/InStock"
    },
    {
      "@type": "Offer",
      "name": "Video Call Verification",
      "price": "49",
      "priceCurrency": "GBP",
      "description": "Identity verification via scheduled video call with our team",
      "availability": "https://schema.org/InStock"
    },
    {
      "@type": "Offer",
      "name": "In-Person Verification",
      "price": "59",
      "priceCurrency": "GBP",
      "description": "Identity verification at our Manchester or Ashton-under-Lyne office",
      "availability": "https://schema.org/InStock"
    }
  ]
};

// Confirmation Statement (CS01) filing service — distinct Service entity so the
// /filing and /pricing pages can advertise it for organic/rich results rather
// than folding it under the identity-verification service.
export const confirmationStatementServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Confirmation Statement Filing (CS01)",
  "provider": {
    "@type": "Organization",
    "name": "TaxStats ID",
    "url": BASE_URL
  },
  "description": "We prepare and file your annual Confirmation Statement (CS01) with Companies House as an Authorised Corporate Service Provider (ACSP AP006589).",
  "areaServed": "GB",
  "serviceType": "Confirmation Statement Filing",
  "offers": [
    {
      "@type": "Offer",
      "name": "One-off Confirmation Statement filing",
      "price": "100",
      "priceCurrency": "GBP",
      "description": "Already verified? We file this year's Confirmation Statement for you.",
      "availability": "https://schema.org/InStock"
    },
    {
      "@type": "Offer",
      "name": "Annual Confirmation Statement filing",
      "price": "89",
      "priceCurrency": "GBP",
      "description": "We file every Confirmation Statement that becomes due during your subscription. Cancel anytime.",
      "availability": "https://schema.org/InStock"
    }
  ]
};

// PSC Verification Statement (VS01) filing service — surfaces the "provide PSC
// verification" intent (gov.uk/guidance/provide-psc-verification) as its own
// Service rather than an add-on buried under CS filing.
export const pscFilingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "PSC Verification Statement Filing (VS01)",
  "provider": {
    "@type": "Organization",
    "name": "TaxStats ID",
    "url": BASE_URL
  },
  "description": "We file your PSC verification statement (VS01) with Companies House, confirming a Person with Significant Control has completed identity verification.",
  "areaServed": "GB",
  "serviceType": "PSC Verification Statement Filing",
  "offers": [
    {
      "@type": "Offer",
      "name": "One-off VS01 filing",
      "price": "60",
      "priceCurrency": "GBP",
      "description": "File this year's PSC verification statement (VS01) on its own.",
      "availability": "https://schema.org/InStock"
    },
    {
      "@type": "Offer",
      "name": "Annual VS01 filing",
      "price": "49",
      "priceCurrency": "GBP",
      "description": "We file your PSC verification statement every year. Cancel anytime.",
      "availability": "https://schema.org/InStock"
    }
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "TaxStats ID",
  "image": `${BASE_URL}/logo.png`,
  "url": BASE_URL,
  "telephone": "+44-161-317-8556",
  "priceRange": "£39-£59",
  "address": [
    {
      "@type": "PostalAddress",
      "streetAddress": "Work.Life CORE, 30 Brown Street",
      "addressLocality": "Manchester",
      "postalCode": "M2 1DH",
      "addressCountry": "GB"
    },
    {
      "@type": "PostalAddress",
      "streetAddress": "56 Oldham Road",
      "addressLocality": "Ashton-under-Lyne",
      "postalCode": "OL6 7AP",
      "addressCountry": "GB"
    }
  ],
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "17:00"
  }
};

/**
 * Create FAQ schema from array of Q&A objects
 * @param {Array<{question: string, answer: string}>} faqs
 * @returns {Object} FAQPage schema
 */
export function createFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

/**
 * Create Article schema for long-form guides (E-E-A-T: author + publisher +
 * published/modified dates). Author defaults to the TaxStats ID organisation —
 * an honest team byline, no invented person.
 * @param {{headline: string, description: string, url: string, datePublished: string, dateModified?: string, image?: string}} p
 * @returns {Object} Article schema
 */
export function createArticleSchema({ headline, description, url, datePublished, dateModified, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "mainEntityOfPage": { "@type": "WebPage", "@id": url.startsWith('http') ? url : `${BASE_URL}${url}` },
    "image": image || `${BASE_URL}/icons/icon-512x512.png`,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": { "@type": "Organization", "name": "TaxStats ID", "url": BASE_URL },
    "publisher": {
      "@type": "Organization",
      "name": "TaxStats ID",
      "logo": { "@type": "ImageObject", "url": `${BASE_URL}/logo.png` }
    }
  };
}

/**
 * Create breadcrumb schema
 * @param {Array<{name: string, url: string}>} items
 * @returns {Object} BreadcrumbList schema
 */
export function createBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  };
}

/**
 * Create HowTo schema for verification process
 * @param {string} name - Name of the process
 * @param {string} description - Description of the process
 * @param {Array<{name: string, text: string}>} steps
 * @returns {Object} HowTo schema
 */
export function createHowToSchema(name, description, steps) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    })),
    "totalTime": "PT15M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "minValue": 39,
      "maxValue": 59
    }
  };
}

/**
 * Create VideoObject schema for an embedded YouTube walkthrough video.
 * Google requires name, description, thumbnailUrl and uploadDate for video
 * rich results — all are sourced from the video registry (src/lib/videos.js).
 * @param {Object} options
 * @param {string} options.videoId - YouTube video id
 * @param {string} options.title
 * @param {string} options.description
 * @param {string} options.uploadDate - ISO date (YYYY-MM-DD)
 * @returns {Object} VideoObject schema
 */
export function createVideoSchema({ videoId, title, description, uploadDate }) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": title,
    "description": description,
    "thumbnailUrl": [`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`],
    ...(uploadDate ? { "uploadDate": uploadDate } : {}),
    "contentUrl": `https://www.youtube.com/watch?v=${videoId}`,
    "embedUrl": `https://www.youtube-nocookie.com/embed/${videoId}`,
    "publisher": {
      "@type": "Organization",
      "name": "TaxStats ID",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo.png`
      }
    }
  };
}

/**
 * Create WebPage schema
 * @param {Object} options
 * @returns {Object} WebPage schema
 */
export function createWebPageSchema({ name, description, url, breadcrumb = null }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "TaxStats ID",
      "url": BASE_URL
    },
    "provider": organizationSchema
  };

  if (breadcrumb) {
    schema.breadcrumb = breadcrumb;
  }

  return schema;
}
